import React, { useState, useEffect } from 'react';
import DataImportExportView from './DataImportExportView';

const SettingsView = ({ dataService, dataPath, onDataPathChange, onDataChange }) => {
  const [isChangingPath, setIsChangingPath] = useState(false);
  const [supportsFileSystemAccess, setSupportsFileSystemAccess] = useState(false);

  useEffect(() => {
    setSupportsFileSystemAccess(dataService.supportsFileSystemAccess());
  }, [dataService]);

  const handleChangeDataPath = async () => {
    setIsChangingPath(true);
    try {
      const newPath = await dataService.changeDataPath();
      if (newPath) {
        onDataPathChange(newPath);
      }
    } catch (error) {
      console.error('Error changing data path:', error);
      alert('Error selecting folder: ' + error.message);
    } finally {
      setIsChangingPath(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">Settings</h2>
      </div>
      
      <div className="p-6 space-y-6">
        {/* Privacy Notice */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="font-medium text-green-900 mb-1">Your Data Stays Private & Local</h3>
              <p className="text-sm text-green-800 mb-2">
                This application operates entirely on your device. All data processing happens locally - nothing is sent to external servers.
              </p>
              <div className="text-xs text-green-700 space-y-1">
                <div className="flex items-center space-x-2">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>{supportsFileSystemAccess ? 'Files stored directly on your local file system' : 'Data stored in your browser\'s secure local storage'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>Works completely offline after initial load</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>You have full control over your data location and access</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Data Source Settings */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Data Source</h3>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Current Data Storage</h4>
                <p className="text-sm text-gray-600 mt-1 break-all">
                  {dataPath || 'No data storage selected'}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  {supportsFileSystemAccess 
                    ? 'Choose a local folder to store your YAML data files.'
                    : 'Data is stored in your browser storage. Use import/export below to manage files.'}
                </p>
              </div>
              {supportsFileSystemAccess && (
                <button
                  onClick={handleChangeDataPath}
                  disabled={isChangingPath}
                  className="ml-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                >
                  {isChangingPath ? 'Selecting...' : dataPath ? 'Change Folder' : 'Select Folder'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Import/Export and CSV Skills Template */}
        <DataImportExportView 
          dataService={dataService} 
          onDataChange={onDataChange}
          supportsFileSystemAccess={supportsFileSystemAccess}
        />

        {/* Data Structure Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Data Structure</h3>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Expected File Structure</h4>
            <div className="text-sm font-mono text-gray-700 bg-white p-3 rounded border">
              <div>your-data-folder/</div>
              <div className="ml-4">├── capabilities.yaml</div>
              <div className="ml-4">├── skills.yaml</div>
              <div className="ml-4">└── people/</div>
              <div className="ml-8">├── person1.yaml</div>
              <div className="ml-8">├── person2.yaml</div>
              <div className="ml-8">└── ...</div>
            </div>
            
            <div className="mt-4 text-sm text-gray-600">
              <p className="mb-2"><strong>capabilities.yaml:</strong> Contains all capability definitions with required skills.</p>
              <p className="mb-2"><strong>skills.yaml:</strong> Contains all skill definitions with categories and related certifications.</p>
              <p><strong>people/*.yaml:</strong> Individual files for each person containing their skills and certifications.</p>
            </div>
          </div>
        </div>

        {/* Sync Information - only show for File System Access API */}
        {supportsFileSystemAccess && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Cloud Integration</h3>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 mb-3">Using with Cloud Storage</h4>
              <div className="text-sm text-blue-800 space-y-2">
                <p>To use this app with cloud storage services:</p>
                <ol className="list-decimal list-inside ml-4 space-y-1">
                  <li>Set up your cloud sync (OneDrive, Google Drive, etc.) on your computer</li>
                  <li>Create or select a synced folder for your skills data</li>
                  <li>Use "Select Folder" above to point to that synced folder</li>
                  <li>All changes will automatically sync across your devices</li>
                </ol>
                <p className="mt-3 font-medium">
                  ⚠️ Make sure all team members have the same folder structure and file permissions.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Export Options */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Export & Backup</h3>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-4">
              Your data is stored in plain YAML files that can be easily backed up or version controlled.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded border">
                <h5 className="text-sm font-medium text-gray-900">Manual Backup</h5>
                <p className="text-xs text-gray-600 mt-1">
                  Simply copy your data folder to create a backup.
                </p>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h5 className="text-sm font-medium text-gray-900">Version Control</h5>
                <p className="text-xs text-gray-600 mt-1">
                  Initialize a git repository in your data folder to track changes.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* App Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">About</h3>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 space-y-2">
              <p><strong>Skills Mapping App</strong> - Version 1.0.0</p>
              <p>A web application for mapping skills to capabilities and people.</p>
              <p className="pt-2 border-t border-gray-200">
                Built with React, TailwindCSS, and modern web APIs.
              </p>
              {supportsFileSystemAccess ? (
                <p className="text-green-600 font-medium">
                  ✅ Your browser supports modern file system access
                </p>
              ) : (
                <p className="text-orange-600 font-medium">
                  ⚠️ Using fallback mode - use import/export for file management
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsView;